import { useParams } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { Link } from '@/components/layout/TransitionLink'
import { Skeleton } from '@/components/ui/skeleton'
import { RankedList } from '@/components/dashboard/RankedList'
import { SongLeaderboard } from '@/components/instructor/SongLeaderboard'
import { EssentialsBrowser } from '@/components/instructor/EssentialsBrowser'
import { PageHero } from '@/components/dashboard/fx/PageHero'
import { Reveal } from '@/components/dashboard/fx/Reveal'
import { SectionHeading } from '@/components/dashboard/fx/SectionHeading'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'
import { PAGE_WIDTH, SECTION } from '@/components/dashboard/fx/layout'
import { useInstructorThemeStats } from '@/hooks/useInstructorThemeStats'
import { usePlaylistOptions } from '@/hooks/usePlaylistOptions'
import { buildUrl, pickRecipe } from '@/lib/buildLink'
import { formatPrecise } from '@/lib/format'
import { workoutNouns } from '@/lib/discipline'

// A theme like "80s" can span an instructor's classes across more than
// one discipline, so "workouts" rather than "rides".
const { plural: WORKOUTS } = workoutNouns('all')

function RowSkeletons({ rows }: { rows: number }) {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-14 w-full rounded-xl" />
      ))}
    </div>
  )
}

export function ThemeDeepDivePage() {
  const { id, theme } = useParams<{ id: string; theme: string }>()
  const { data, error } = useInstructorThemeStats(id, theme)
  const { data: options } = usePlaylistOptions(id ?? null)
  const recipe = pickRecipe(options, theme ?? null)

  return (
    <div className="overflow-x-clip">
      <PageHero
        back={{ to: `/instructors/${id}`, label: `Back to ${data?.instructor.name ?? 'instructor'}` }}
        eyebrow="Theme deep dive"
        title={data ? `${data.instructor.name} ·` : 'Loading'}
        accent={data?.theme}
        description={
          data && (
            <>
              {formatPrecise(data.ride_count)} {WORKOUTS} tagged &ldquo;{data.theme}&rdquo; so far
            </>
          )
        }
      >
        {recipe?.theme && id && (
          <div className={`${PAGE_WIDTH} pb-10 sm:pb-14`}>
            <Link
              to={buildUrl(id, recipe)}
              className="group inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              Build a {data?.theme ?? recipe.theme} ride{data ? ` from ${data.instructor.name}'s music` : ''}
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        )}
      </PageHero>

      {error && (
        <div className="mx-auto mt-8 max-w-7xl px-6">
          <p className="rounded-2xl border border-destructive/50 p-4 text-sm text-destructive">
            Couldn&apos;t load this theme: {error}
          </p>
        </div>
      )}

      <section className={SECTION}>
        <SectionHeading
          index="01"
          title="Go-to"
          accent="tracks."
          blurb={`The songs and artists ${data ? `${data.instructor.name} reaches` : 'they reach'} for most in ${theme} ${WORKOUTS}.`}
        />
        <div className="grid grid-cols-1 gap-x-16 gap-y-12 lg:grid-cols-2">
          <div>
            <h3 className="mb-2 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Go-to songs</h3>
            {data ? <SongLeaderboard songs={data.top_songs} /> : <RowSkeletons rows={8} />}
          </div>
          <div>
            <h3 className="mb-2 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Go-to artists</h3>
            {data ? (
              <RankedList
                unit={WORKOUTS}
                entries={data.top_artists.map((a) => ({ id: a.id, name: a.name, image_url: a.image_url, count: a.play_count }))}
              />
            ) : (
              <RowSkeletons rows={8} />
            )}
          </div>
        </div>
      </section>

      <section className={SECTION}>
        <SectionHeading
          index="02"
          title="Essentials"
          accent="still missing."
          blurb={`${data?.theme ?? theme} tracks the wider Last.fm community loves that haven't shown up in the rotation yet -- from top essentials to genuine deep cuts.`}
        />
        <Reveal>
          <SpotlightCard className="p-6 sm:p-8">
            {data ? (
              data.missing_essentials.length > 0 ? (
                <EssentialsBrowser essentials={data.missing_essentials} />
              ) : (
                <p className="text-muted-foreground">No canonical gap data available for &ldquo;{data.theme}&rdquo; yet.</p>
              )
            ) : (
              <RowSkeletons rows={6} />
            )}
          </SpotlightCard>
        </Reveal>
      </section>
    </div>
  )
}
